import React, { useEffect, useState, useRef } from 'react';
import {
    makeStyles,
    shorthands,
    Button,
    Caption1,
    tokens,
    Text,
    Title1,
    Card,
    CardHeader,
    CardPreview,
    CardProps,
    Title3,
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular, DismissRegular } from "@fluentui/react-icons";

import { useSignInState } from './Components/signInState';
import ServiceHealthHome from './Components/ServiceHealth';
import useFetch from './Hooks/useFetchAPI';
import { Link } from 'react-router-dom';
import { useConfig } from './context/ConfigContext';

const useStyles = makeStyles({
    main: {
        ...shorthands.gap("16px"),
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        paddingLeft: "16px",
        alignItems: "flex-start",
        boxSizing: "border-box",
        // height: "calc(100vh - 120px)",
        "@media (max-width: 1200px)": {
            "&": {
                gridTemplateColumns: "2fr 1fr"
            }
        },
        "@media (max-width: 700px)": {
            "&": {
                display: "flex",
                flexDirection: "column",
                gridTemplateColumns: "1fr",
                gridTemplateRows: "auto auto",
                rowGap: "16px",
            }
        },
    },
    cardsContainer: {
        gridColumn: "span 3",
        flexDirection: "column",
        overflowY: "auto",
        height: "calc(100vh - 300px)",
        flexGrow: 1,
        flex: 1,
        paddingTop: "16px",
        paddingRight: "16px",
        paddingBottom: "32px",
        boxSizing: "border-box",
        "@media (max-width: 700px)": {
            "&": {
                gridRow: "2",
                gridColumn: "1",
                marginTop: "0px",
            }
        },
    },
    cards: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
        ...shorthands.gap("16px"),
        paddingBottom: "16px",
    },
    card: {
        width: "350px",
        maxWidth: "100%",
        height: "fit-content",
        "@media (max-width: 450px)": {
            "&": {
                width: "330px",
            }
        },
    },
    serviceHealth: {
        gridColumnStart: 4,
        gridRow: "span 2",
        minWidth: "220px",
        "@media (max-width: 700px)": {
            "&": {
                gridRow: "1",
                gridColumn: "1",
                marginBottom: "16px",
            }
        },
    },
    caption: {
        color: tokens.colorNeutralForeground3,
    },

    smallRadius: {
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
    },

    grayBackground: {
        backgroundColor: tokens.colorNeutralBackground3,
    },

    logoBadge: {
        ...shorthands.padding("5px"),
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
        backgroundColor: "#FFF",
        boxShadow:
            "0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
    },
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
    link: {
        color: tokens.colorNeutralForeground1,

        ":hover": {
            color: tokens.colorNeutralForeground1,
            textDecoration: "none",
        },
    },
});

interface SelectableCardProps extends CardProps {
    image: string;
    imagealt?: string;
    logo?: string;
    logoalt?: string;
    header: string;
    description?: string;
    link: string;
    newwindow: boolean;
}

const intents: MessageBarIntent[] = ["info", "warning", "error", "success"];

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

interface License {
    LicenseID: number;
    ExpiryDate: string;
    StartDate: string;
    IsActive: boolean;
    CustomerID: number;
}

const importImage = (imageName: string, fallback: string): string => {
    try {
        return require(`./Assets/cards/${imageName}`);
    } catch (err) {
        return require(`./Assets/cards/${fallback}`);
    }
};

const SelectableCard: React.FC<SelectableCardProps> = (props) => {
    const styles = useStyles();
    const linkRef = React.useRef<HTMLAnchorElement>(null);

    return (
        <Card className={styles.card} {...props} focusMode="off">
            <CardPreview className={styles.grayBackground}
                logo={
                    <img
                        className={styles.logoBadge}
                        src={importImage(props.logo || "carddefaultlogo.png", "carddefaultlogo.png")}
                        alt={props.logoalt || "Card Logo"}
                    />
                }
            >
                <img
                    className={styles.smallRadius}
                    src={importImage(props.image || "carddefaultimage.png", "carddefaultimage.png")}
                    alt={props.imagealt || "Card Image"}
                />
            </CardPreview>

            <CardHeader
                header={
                    <Link
                        to={props.link}
                        target={props.newwindow ? "_blank" : undefined}
                        ref={linkRef}
                        className={styles.link}
                    >
                        <Text weight="semibold">{props.header}</Text>
                    </Link>
                }
                description={
                    <Caption1 className={styles.caption}>{props.description}</Caption1>
                }
                action={
                    <Button
                        appearance="transparent"
                        icon={<MoreHorizontal20Regular />}
                        aria-label="More actions"
                    />
                }
            />
        </Card>
    );
};

export const WelcomePage: React.FC = () => {
    const styles = useStyles();

    const { signInState } = useSignInState();
    const { appName } = useConfig();
    const [selected, setSelected] = React.useState(false);
    const [selected1, setSelected1] = React.useState(false);
    const [selected2, setSelected2] = React.useState(false);

    const [messages, setMessages] = useState<NotificationMessage[]>([]);
    const [cards, setCards] = useState<HomepageCardData[]>([]);
    const counterRef = useRef(0);

    const { fetchAPI: fetchCards, apiData: cardsData, isLoading, serverError } = useFetch({ scope: 'AppUser' })


    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId)
        );

    const currentHour = new Date().getHours();
    let greeting;

    if (currentHour >= 3 && currentHour < 12) {
        greeting = "Good morning";
    } else if (currentHour >= 12 && currentHour < 17) {
        greeting = "Good afternoon";
    } else if (currentHour >= 17 && currentHour < 20) {
        greeting = "Good evening";
    } else {
        greeting = "Working late";
    }

    useEffect(() => {
        const fetchCardsList = async () => {
            await fetchCards({ url: 'general/getcards', method: 'GET' });
        };
        console.log('Card Data:', cardsData);
        fetchCardsList()
            .catch((error) => {
                console.error('Error fetching cards', error);
                setCards([]);
            });
    }, []);

    useEffect(() => {
        console.log('[HomePage] Cards:', cardsData);
        if (cardsData) {
            setCards(cardsData as unknown as HomepageCardData[]);
        }
    }, [cardsData]);

    useEffect(() => {
        // Dismiss all existing messages first
        setMessages([]);
        const now = new Date();
        const currentCustomerID = signInState.currentCustomerID;

        // Only consider licenses that match the current customer
        const licensesForCurrentCustomer = signInState.licenseInfo.filter(
            (license) => license.CustomerID === currentCustomerID
        );

        // Helper to check if a given license has a 'replacement' that starts
        // exactly on its expiry date for the same customer.
        const hasReplacement = (license: License, allLicenses: License[]): boolean => {
            const expiryTime = new Date(license.ExpiryDate).getTime();
            return allLicenses.some(
                (other) =>
                    other.LicenseID === license.LicenseID &&
                    other.CustomerID === license.CustomerID && // match the same customer
                    new Date(other.StartDate).getTime() === expiryTime
            );
        };

        // Filter down to active licenses (for the current customer) that do not
        // have a replacement starting on their expiry date.
        const activeLicenses = licensesForCurrentCustomer.filter(
            (license) => license.IsActive && !hasReplacement(license, licensesForCurrentCustomer)
        );

        if (activeLicenses.length === 0) {
            addMessage("There are no active licenses for this account.", "error");
            return;
        } else {
            // Check if any has expired
            const expired = activeLicenses.some((license) => {
                const expiryDate = new Date(license.ExpiryDate);
                return expiryDate < now;
            });
            if (expired) {
                addMessage(
                    "Some of your licenses have expired. Please check the Licenses page for details.",
                    "error"
                );
            } else {
                // Check if any is expiring within 7 days
                const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                const expiringSoon = activeLicenses.some((license) => {
                    const expiryDate = new Date(license.ExpiryDate);
                    return expiryDate < sevenDaysFromNow;
                });

                if (expiringSoon) {
                    addMessage(
                        "Some of your licenses are expiring soon. Please check the Licenses page for details.",
                        "warning"
                    );
                }
            }
        }
    }, [signInState.currentCustomerID, signInState.licenseInfo]);

    return (
        <>
            <MessageBarGroup className={styles.messageBarGroup}>
                {messages.map(({ intent, id, message }) => (
                    <MessageBar key={`${intent}-${id}`} intent={intent}>
                        <MessageBarBody>
                            <MessageBarTitle>Account needs attention</MessageBarTitle>
                            {message}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    onClick={() => dismissMessage(id)}
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular />}
                                />
                            }
                        />
                    </MessageBar>
                ))}
            </MessageBarGroup>
            <div style={{ margin: '20px' }}>
                <Title1> {greeting} {signInState.userInfo[0]?.FirstName}{greeting === 'Working late' && '?'} </Title1>
            </div>
            <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
                <p />
                <Text>
                    Welcome to {appName} 🎉 </Text>
                <br />
                <Text>   We're constantly looking to improve and add new features and would love to hear your feedback, please use the feedback button in the bottom left corner.
                </Text>
                <p />
                <Text>
                    This home screen will keep you up-to-date with latest news and things to check out.
                </Text>
                <br />
                <Text>
                    Use the navigation on the left to go explore and keep your eye on the home page as things will change quickly!
                </Text>
            </div>

            <p />
            <div className={styles.main}>

                <div className={styles.cardsContainer}>
                    <div className={styles.cards}>
                        {cardsData && Array.isArray(cardsData.result) && cardsData.result.map((card) => (
                            <SelectableCard
                                key={card.id}
                                onSelectionChange={(_, { selected }) => setSelected(selected)}
                                header={card.header}
                                description={card.description}
                                image={card.image}
                                link={card.link}
                                newwindow={card.newwindow}
                            />
                        ))}
                    </div>
                </div>

                <div className={styles.serviceHealth}>
                    <ServiceHealthHome />
                </div>

            </div>
        </>
    );
};

